import { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import { computeAdoptionStats } from "../../../../Utils/appVersionAdoptionUtils";
import {
  buildMonthBuckets,
  DASHBOARD_PERIODS,
  getDeltaPercent,
  getMonthLabel,
  getPeriodBounds,
  parseDateToDayjs,
  parseNumber,
} from "../utils/monthlySeries";
import { USER_SEGMENTS, getSegmentKeyForUser } from "../utils/segments";
import { isCapitalRoutingRow } from "../../../../Utils/fundsPeriodTotals";

const emptySegmentMetrics = () => ({
  clients: 0,
  aum: 0,
  deposits: 0,
  withdrawals: 0,
  routingIn: 0,
  routingOut: 0,
  netFlow: 0,
  performancePaid: 0,
  yieldParticipants: 0,
  yieldAdoption: 0,
  netYield: 0,
  grossYield: 0,
  yieldRevenue: 0,
  appUpToDate: 0,
  appOutdated: 0,
  appUnknown: 0,
  appAdoptionPct: 0,
  monthly: [],
  deltas: { netFlow: null, performancePaid: null },
});

const EMPTY_COMPANY = {
  totalClients: 0,
  totalAum: 0,
  totalDeposits: 0,
  totalWithdrawals: 0,
  totalNetFlow: 0,
  fundPerformance: 0,
  monthlyPerformance: [],
  segmentsAum: [],
};

/** Valor efetivo de um saque: offered_amount (se > 0), senão total_amount, senão withdraw_amount. */
const getWithdrawalAmount = (txn) => {
  const offered = parseNumber(txn?.offered_amount);
  if (offered > 0) return offered;
  const total = parseNumber(txn?.total_amount);
  if (total > 0) return total;
  return parseNumber(txn?.withdraw_amount);
};

/** Data em que o saque foi efetivamente pago (não a data do pedido). */
const getWithdrawalDate = (txn) =>
  txn?.request_approval_time || txn?.auto_pay_paid_at || txn?.conclusion_date || txn?.date;

/** Data efetiva de um depósito aprovado. */
const getDepositDate = (txn) => txn?.conclusion_date || txn?.date || txn?.created_at;

/** Valor de performance paga por entrada de ledger (type 3). */
const getPerformanceAmount = (ledger) =>
  parseNumber(ledger?.new_txn_amount ?? ledger?.profit_loss ?? ledger?.amount);

const buildScaffold = (bounds) =>
  buildMonthBuckets(bounds).map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    deposits: 0,
    withdrawals: 0,
    netFlow: 0,
    performance: 0,
    aum: 0,
  }));

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  const parsed = parseDateToDayjs(value);
  return parsed ? parsed.valueOf() : 0;
};

/**
 * Meses reais de performance do fundo direto da coleção crua `yearly_graph`
 * (mesma lógica do app: dedup por mês/ano mantendo o mais recente por created_at,
 * ordenado cronologicamente). O documento consolidado pode estar desatualizado,
 * então lemos a coleção crua para bater com o app.
 */
const buildRealPerformanceMonths = (yearlyDocs) => {
  const monthlyMap = new Map();
  (yearlyDocs || []).forEach((entry) => {
    if (!entry?.month || !entry?.year) return;
    const key = `${entry.month}-${entry.year}`;
    const existing = monthlyMap.get(key);
    if (!existing || toMillis(entry.created_at) > toMillis(existing.created_at)) {
      monthlyMap.set(key, entry);
    }
  });

  return [...monthlyMap.values()]
    .map((entry) => {
      const key = `${entry.year}-${String(entry.month).padStart(2, "0")}`;
      return {
        key,
        label: getMonthLabel(key),
        amount: parseNumber(entry.amount),
        sortKey: entry.year * 100 + entry.month,
      };
    })
    .sort((a, b) => a.sortKey - b.sortKey);
};

/**
 * Seleciona os meses de performance conforme o período escolhido.
 * Para períodos por contagem de meses usamos as N entradas mais recentes
 * (igual ao `take(12)` do app); YTD/Custom filtram por intervalo real.
 */
const selectPerformanceMonths = (months, period, bounds) => {
  switch (period) {
    case DASHBOARD_PERIODS.ALL_TIME:
      return months;
    case DASHBOARD_PERIODS.LAST_MONTH:
      return months.slice(-1);
    case DASHBOARD_PERIODS.LAST_24_MONTHS:
      return months.slice(-24);
    case DASHBOARD_PERIODS.YTD: {
      const startKey = bounds.start.year() * 100 + (bounds.start.month() + 1);
      const endKey = bounds.end.year() * 100 + (bounds.end.month() + 1);
      return months.filter((month) => month.sortKey >= startKey && month.sortKey <= endKey);
    }
    case DASHBOARD_PERIODS.CUSTOM: {
      const startKey = bounds.start.year() * 100 + (bounds.start.month() + 1);
      const endKey = bounds.end.year() * 100 + (bounds.end.month() + 1);
      return months.filter((month) => month.sortKey >= startKey && month.sortKey <= endKey);
    }
    case DASHBOARD_PERIODS.LAST_12_MONTHS:
    default:
      return months.slice(-12);
  }
};

const aggregate = ({
  users,
  transactions,
  ledgers,
  monthlyClosings,
  yearlyDocs,
  yieldFinals,
  appTargets,
  bounds,
  period,
}) => {
  const activeClients = users.filter((user) => user.role === 1 && user.status === 1);
  const usersById = new Map(users.map((user) => [user.id, user]));

  // Estado inicial por segmento
  const bySegment = {};
  const monthlyBySegment = {};
  const monthKeyIndex = {};
  USER_SEGMENTS.forEach((segment) => {
    bySegment[segment.key] = emptySegmentMetrics();
    const scaffold = buildScaffold(bounds);
    monthlyBySegment[segment.key] = scaffold;
    monthKeyIndex[segment.key] = new Map(scaffold.map((bucket, index) => [bucket.key, index]));
  });

  // Contagem de clientes e AUM (snapshot atual)
  activeClients.forEach((user) => {
    const key = getSegmentKeyForUser(user);
    const segment = bySegment[key];
    if (!segment) return;
    segment.clients += 1;
    segment.aum += parseNumber(user.net_balance);
    if (user.yield_active === true) segment.yieldParticipants += 1;
  });

  // Fluxo mensal (depósitos e saques) por segmento
  const inBounds = (dateValue) => {
    const parsed = parseDateToDayjs(dateValue);
    if (!parsed) return null;
    if (parsed.isBefore(bounds.start) || parsed.isAfter(bounds.end)) return null;
    return parsed.startOf("month").format("YYYY-MM");
  };

  transactions.forEach((txn) => {
    const user = usersById.get(txn.user_id);
    const key = user ? getSegmentKeyForUser(user) : null;
    if (!key || !bySegment[key]) return;
    const isRouting = isCapitalRoutingRow(txn);

    if (txn.request_type === 0 && txn.request_status === 2) {
      const monthKey = inBounds(getDepositDate(txn));
      if (!monthKey) return;
      const amount = parseNumber(txn.total_amount);
      if (isRouting) {
        bySegment[key].routingIn += amount;
        return;
      }
      bySegment[key].deposits += amount;
      const idx = monthKeyIndex[key].get(monthKey);
      if (idx !== undefined) monthlyBySegment[key][idx].deposits += amount;
    }

    if (txn.request_type === 1 && txn.request_status === 5) {
      const monthKey = inBounds(getWithdrawalDate(txn));
      if (!monthKey) return;
      const amount = getWithdrawalAmount(txn);
      if (isRouting) {
        bySegment[key].routingOut += amount;
        return;
      }
      bySegment[key].withdrawals += amount;
      const idx = monthKeyIndex[key].get(monthKey);
      if (idx !== undefined) monthlyBySegment[key][idx].withdrawals += amount;
    }
  });

  // Performance paga por segmento (ledgers type 3)
  ledgers.forEach((ledger) => {
    if (ledger.transection_type !== 3) return;
    const user = usersById.get(ledger.user_id);
    const key = user ? getSegmentKeyForUser(user) : null;
    if (!key || !bySegment[key]) return;

    const monthKey = inBounds(ledger.date || ledger.conclusion_date || ledger.created_at);
    if (!monthKey) return;

    const amount = getPerformanceAmount(ledger);
    bySegment[key].performancePaid += amount;
    const idx = monthKeyIndex[key].get(monthKey);
    if (idx !== undefined) monthlyBySegment[key][idx].performance += amount;
  });

  // AUM histórico por segmento (monthly_closings).
  // IMPORTANTE: closing_month é 0-based (0=Jan ... 11=Dez); closing_year é o ano
  // do mês fechado. O campo `date` é a data de criação (dia 1-3 do mês seguinte),
  // então NÃO serve para derivar o mês. Convertendo para 1-based (mês+1).
  monthlyClosings.forEach((closing) => {
    const user = usersById.get(closing.user_id);
    const key = user ? getSegmentKeyForUser(user) : null;
    if (!key || !bySegment[key]) return;

    const year = closing.closing_year;
    const rawMonth = closing.closing_month;
    let monthKey = null;
    if (year != null && rawMonth != null) {
      const monthNum = Number(rawMonth) + 1; // 0-based -> 1-based
      if (monthNum >= 1 && monthNum <= 12) {
        monthKey = `${year}-${String(monthNum).padStart(2, "0")}`;
      }
    }
    const idx = monthKey ? monthKeyIndex[key].get(monthKey) : undefined;
    if (idx === undefined) return;

    const amount = parseNumber(
      closing.closing_balance ?? closing.current_balance ?? closing.net_balance ?? closing.balance
    );
    monthlyBySegment[key][idx].aum += amount;
  });

  // Yield por segmento (yields_final)
  yieldFinals.forEach((record) => {
    if (!Array.isArray(record.participants)) return;
    record.participants.forEach((participant) => {
      const user = usersById.get(participant.user_id);
      const key = user
        ? getSegmentKeyForUser(user)
        : getSegmentKeyForUser({ user_type: participant.user_type });
      if (!key || !bySegment[key]) return;

      bySegment[key].netYield += parseNumber(participant.net_yield ?? participant.total_net_yield);
      bySegment[key].grossYield += parseNumber(
        participant.gross_yield ?? participant.total_gross_yield
      );
      bySegment[key].yieldRevenue += parseNumber(participant.total_commission);
    });
  });

  // Adoção de app por segmento
  const adoption = computeAdoptionStats(activeClients, appTargets.ios, appTargets.android);
  const adoptionByKey = {
    individual: adoption.individual,
    corporate: adoption.corporate,
  };

  // Finaliza cada segmento
  USER_SEGMENTS.forEach((segment) => {
    const metrics = bySegment[segment.key];
    const monthly = monthlyBySegment[segment.key];

    // AUM evolution: preenche meses sem fechamento com o último valor conhecido
    // (carry-forward) para não cair a zero; o mês atual (ainda não fechado) usa o
    // snapshot atual de net_balance para casar com o KPI de AUM.
    let lastKnownAum = 0;
    monthly.forEach((bucket) => {
      if (bucket.aum > 0) {
        lastKnownAum = bucket.aum;
      } else {
        bucket.aum = lastKnownAum;
      }
    });
    if (monthly.length > 0) {
      monthly[monthly.length - 1].aum = metrics.aum;
    }

    monthly.forEach((bucket) => {
      bucket.netFlow = bucket.deposits - bucket.withdrawals;
    });

    metrics.netFlow = metrics.deposits - metrics.withdrawals;
    metrics.monthly = monthly;
    metrics.yieldAdoption = metrics.clients
      ? (metrics.yieldParticipants / metrics.clients) * 100
      : 0;

    const adoptionBucket = adoptionByKey[segment.key];
    if (adoptionBucket) {
      metrics.appUpToDate = adoptionBucket.uptodate;
      metrics.appOutdated = adoptionBucket.outdated;
      metrics.appUnknown = adoptionBucket.unknown;
      metrics.appAdoptionPct = adoptionBucket.total
        ? (adoptionBucket.uptodate / adoptionBucket.total) * 100
        : 0;
    }

    const last = monthly[monthly.length - 1] || {};
    const prev = monthly[monthly.length - 2] || {};
    metrics.deltas = {
      netFlow: getDeltaPercent(last.netFlow, prev.netFlow),
      performancePaid: getDeltaPercent(last.performance, prev.performance),
    };
  });

  // Performance do fundo: meses reais da coleção crua yearly_graph (igual ao app).
  const realMonths = buildRealPerformanceMonths(yearlyDocs);
  // Meses do período selecionado (o filtro afeta KPI e gráfico juntos).
  const monthlyPerformance = selectPerformanceMonths(realMonths, period, bounds);
  const fundPerformance = monthlyPerformance.reduce((sum, month) => sum + month.amount, 0);

  const company = {
    ...EMPTY_COMPANY,
    totalClients: USER_SEGMENTS.reduce((sum, s) => sum + bySegment[s.key].clients, 0),
    totalAum: USER_SEGMENTS.reduce((sum, s) => sum + bySegment[s.key].aum, 0),
    totalDeposits: USER_SEGMENTS.reduce((sum, s) => sum + bySegment[s.key].deposits, 0),
    totalWithdrawals: USER_SEGMENTS.reduce((sum, s) => sum + bySegment[s.key].withdrawals, 0),
    monthlyPerformance,
    fundPerformance,
    segmentsAum: USER_SEGMENTS.map((segment) => ({
      key: segment.key,
      label: segment.label,
      accent: segment.accent,
      aum: bySegment[segment.key].aum,
    })),
  };
  company.totalNetFlow = company.totalDeposits - company.totalWithdrawals;

  return { company, bySegment };
};

const useDashboardData = ({ period = DASHBOARD_PERIODS.LAST_12_MONTHS, startDate, endDate }) => {
  const [state, setState] = useState({
    isLoading: true,
    error: null,
    company: EMPTY_COMPANY,
    bySegment: USER_SEGMENTS.reduce((acc, segment) => {
      acc[segment.key] = emptySegmentMetrics();
      return acc;
    }, {}),
    lastUpdated: null,
  });

  const bounds = useMemo(
    () => getPeriodBounds({ period, startDate, endDate }),
    [period, startDate, endDate]
  );

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setState((previous) => ({ ...previous, isLoading: true, error: null }));

      try {
        const [
          usersSnapshot,
          transactionsSnapshot,
          ledgersSnapshot,
          monthlyClosingsSnapshot,
          yieldFinalSnapshot,
          appVersionSnapshot,
          yearlyGraphSnapshot,
        ] = await Promise.all([
          getDocs(collection(DATABASE, "users")),
          getDocs(collection(DATABASE, "transection_requests")),
          getDocs(collection(DATABASE, "ledgers")),
          getDocs(collection(DATABASE, "monthly_closings")),
          getDocs(collection(DATABASE, "yields_final")),
          getDocs(collection(DATABASE, "app_version")),
          getDocs(collection(DATABASE, "yearly_graph")),
        ]);

        if (!isMounted) return;

        const appDoc = appVersionSnapshot.docs[0]?.data() || {};
        const appTargets = {
          ios: parseInt(appDoc.ios_build_number, 10) || 0,
          android: parseInt(appDoc.android_build_number, 10) || 0,
        };

        const aggregated = aggregate({
          users: usersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          transactions: transactionsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          ledgers: ledgersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          monthlyClosings: monthlyClosingsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          yearlyDocs: yearlyGraphSnapshot.docs.map((doc) => doc.data()),
          yieldFinals: yieldFinalSnapshot.docs.map((doc) => doc.data()),
          appTargets,
          bounds,
          period,
        });

        setState({
          ...aggregated,
          isLoading: false,
          error: null,
          lastUpdated: new Date(),
        });
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        if (!isMounted) return;
        setState((previous) => ({ ...previous, isLoading: false, error }));
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [bounds, period]);

  return { ...state, bounds };
};

export default useDashboardData;
export { DASHBOARD_PERIODS };
